import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3004';

const AuthVerify = ({ onLogin }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setStatus('error');
      setError('Invalid verification link. No token provided.');
      return;
    }

    verifyToken(token);
  }, [searchParams]);

  const verifyToken = async (token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/verify/${token}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Verification failed');
      }

      setUser(data.user);
      setStatus('success');

      // Call onLogin to update app state
      if (onLogin) {
        onLogin(data.user);
      }

      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/');
      }, 2000);

    } catch (err) {
      console.error('Verification error:', err);
      setStatus('error');
      setError(err.message || 'Failed to verify link. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex flex-col">
      {/* Header */}
      <header className="w-full py-4 px-6 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2">
            <img
              src="/bloodResults.png"
              alt="Blood Results Logo"
              className="h-8 w-auto"
            />
            <span className="text-lg font-semibold text-gray-900">
              Blood Results
            </span>
          </Link>
        </div>
      </header>

      {/* Brand Banner */}
      <div className="w-full py-6 bg-white flex justify-center border-t border-gray-200">
        <img
          src="/blood-results-brand.svg"
          alt="Blood Results Brand"
          className="h-20 w-auto"
        />
      </div>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 border border-gray-100 text-center">
          {status === 'verifying' && (
            <>
              <div className="mb-6">
                <svg className="animate-spin mx-auto h-16 w-16 text-blue-600" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Verifying Your Link...
              </h2>
              <p className="text-gray-600">
                Please wait while we verify your email and sign you in.
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="mb-6">
                <svg className="mx-auto h-16 w-16 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Welcome{user ? `, ${user.firstName}` : ''}!
              </h2>
              <p className="text-gray-600 mb-6">
                Your email has been verified successfully. You're now signed in.
              </p>
              <p className="text-sm text-gray-500">
                Redirecting to your dashboard...
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="mb-6">
                <svg className="mx-auto h-16 w-16 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Verification Failed
              </h2>
              <p className="text-red-600 mb-6">
                {error}
              </p>
              <div className="space-y-3">
                <Link
                  to="/"
                  className="block w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition duration-200"
                >
                  Request New Link
                </Link>
                <p className="text-sm text-gray-500">
                  or <Link to="/signup" className="text-blue-600 hover:text-blue-700">Sign Up</Link> if you don't have an account
                </p>
              </div>
            </>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="w-full py-6 px-6 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto text-center text-sm text-gray-500">
          <p>&copy; 2025 Blood-Results.co.uk A KNX-Associates Ltd Brand. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default AuthVerify;
